import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Alert,
  IconButton,
  ImageList,
  ImageListItem,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack,
  CloudUpload,
  Delete,
  Phone,
  Email,
} from '@mui/icons-material';
import api from '../../services/api';

interface Category {
  id: number;
  name: string;
  slug: string;
}

const CreateListingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [images, setImages] = useState<File[]>([]);
  const [imagePreview, setImagePreview] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category_id: '',
    location: '',
    contact_phone: '',
    contact_email: '',
    prefer_phone: true,
  });
  
  const [usingDefaultContact, setUsingDefaultContact] = useState(false);

  useEffect(() => {
    // Check if user is authenticated - check both serviceToken and platform token
    const hasServiceToken = localStorage.getItem('serviceToken');
    const isServiceAuthenticated = localStorage.getItem('serviceAuthenticated') === 'true' || !!hasServiceToken;
    const isPlatformAuthenticated = localStorage.getItem('token');
    
    if (!isServiceAuthenticated && !isPlatformAuthenticated) {
      navigate('/app/marketplace');
      return;
    }
    
    // Pre-populate contact info from service user data if available
    const serviceUserData = localStorage.getItem('serviceUserData');
    if (serviceUserData) {
      try {
        const userData = JSON.parse(serviceUserData);
        if (userData.email || userData.phone) {
          setFormData(prev => ({
            ...prev,
            contact_email: userData.email || '',
            contact_phone: userData.phone || '',
          }));
          setUsingDefaultContact(true);
        }
      } catch (e) {
        console.error('Error parsing service user data:', e);
      }
    }
    
    fetchCategories();
  }, [navigate]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/marketplace/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoryChange = (event: any) => {
    setFormData(prev => ({
      ...prev,
      category_id: event.target.value,
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (images.length + files.length > 5) {
      setError('Maximum 5 images allowed');
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        setError(`${file.name} exceeds 5MB limit`);
        return false;
      }
      return true;
    });

    setImages(prev => [...prev, ...validFiles]);
    
    // Create preview URLs
    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    setImagePreview(prev => [...prev, ...newPreviews]);
    setError(null);
  };

  const handleRemoveImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate form
    if (!formData.title || !formData.description || !formData.price) {
      setError(t('marketplace.create.errors.requiredFields'));
      return;
    }
    
    // Validate description length (backend requires minimum 10 characters)
    if (formData.description.length < 10) {
      setError(t('marketplace.create.errors.descriptionTooShort'));
      return;
    }
    
    if (!formData.contact_email && !formData.contact_phone) {
      setError(t('marketplace.create.errors.contactRequired'));
      return;
    }

    setLoading(true);

    try {
      // Create FormData for multipart upload
      const submitData = new FormData();
      submitData.append('listing_data', JSON.stringify({
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        category_id: formData.category_id || null,
        location: formData.location,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email,
        prefer_phone: formData.prefer_phone,
      }));

      // Append images
      images.forEach(image => {
        submitData.append('files', image);
      });

      const response = await api.post('/marketplace/listings', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Success - navigate to the new listing
      navigate(`/app/marketplace/listing/${response.data.id}`);
    } catch (error: any) {
      console.error('Error creating listing:', error);
      setError(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/app/marketplace')}
        sx={{ mb: 2 }}
      >
        {t('marketplace.backToMarketplace')}
      </Button>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('marketplace.create.title')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            required
            label={t('marketplace.create.listingTitle')}
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            margin="normal"
          />

          <TextField
            fullWidth
            required
            multiline
            rows={4}
            label={t('marketplace.create.description')}
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            margin="normal"
            error={formData.description.length > 0 && formData.description.length < 10}
            helperText={
              formData.description.length > 0 && formData.description.length < 10
                ? t('marketplace.create.descriptionHelper.tooShort', { current: formData.description.length, required: 10 })
                : t('marketplace.create.descriptionHelper.minimum')
            }
          />

          <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
            <TextField
              required
              label={t('marketplace.create.price')}
              name="price"
              type="number"
              value={formData.price}
              onChange={handleInputChange}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }
              }}
              sx={{ flex: 1 }}
            />

            <FormControl sx={{ flex: 1 }}>
              <InputLabel>{t('marketplace.search.category')}</InputLabel>
              <Select
                value={formData.category_id}
                onChange={handleCategoryChange}
                label={t('marketplace.search.category')}
              >
                <MenuItem value="">
                  <em>{t('marketplace.create.noCategory')}</em>
                </MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.slug ? t(`marketplace.categories.${category.slug}`, { defaultValue: category.name }) : category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('marketplace.create.location')}
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              sx={{ flex: 1 }}
            />
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            {t('marketplace.create.contactInfo')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label={t('marketplace.create.phone')}
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleInputChange}
              disabled={usingDefaultContact}
              slotProps={{
                input: {
                  readOnly: usingDefaultContact,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{ flex: 1 }}
              helperText={usingDefaultContact ? "Using your account phone" : ""}
            />

            <TextField
              label={t('marketplace.create.email')}
              name="contact_email"
              type="email"
              value={formData.contact_email}
              onChange={handleInputChange}
              disabled={usingDefaultContact}
              slotProps={{
                input: {
                  readOnly: usingDefaultContact,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{ flex: 1 }}
              helperText={usingDefaultContact ? "Using your account email" : ""}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={formData.prefer_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, prefer_phone: e.target.checked }))}
              />
            }
            label={t('marketplace.create.preferPhone')}
          />

          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            {t('marketplace.create.images')} ({images.length}/5)
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              disabled={images.length >= 5}
            >
              {t('marketplace.create.uploadImages')}
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>
          </Box>

          {imagePreview.length > 0 && (
            <ImageList sx={{ height: 200 }} cols={5} rowHeight={164}>
              {imagePreview.map((preview, index) => (
                <ImageListItem key={index} sx={{ position: 'relative' }}>
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    loading="lazy"
                    style={{ height: '100%', objectFit: 'cover' }}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'background.paper' },
                    }}
                    onClick={() => handleRemoveImage(index)}
                  >
                    <Delete />
                  </IconButton>
                </ImageListItem>
              ))}
            </ImageList>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/app/marketplace')}
              disabled={loading}
            >
              {t('marketplace.create.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              sx={{ flex: 1 }}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                t('marketplace.create.submit')
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreateListingPage;